import { ColumnWidthRatio } from '../interfaces/ColumnWidthRatio';
import { OwaDate } from 'owa-datetime';
import { isToday } from 'owa-observable-datetime';

/**
 * Gets the width ratios for the default case where every column is the same width
 * @param columnModels the column models to use for width calculations
 */
export function getDefaultColumnWidthRatios(columnModels: OwaDate[]): ColumnWidthRatio[] {
    let numDays = columnModels.length;
    // if today is not in the week, just return everything at equal widths
    return columnModels.map((model: OwaDate, index: number) => {
        return {
            left: index / numDays,
            right: (numDays - index - 1) / numDays,
        };
    });
}

/**
 * Gets the width ratios for dynamic resizing where today is largest column, tomorrow is second largest, and
 * future days get emphasized more than past days
 * @param columnModels the column models to use for width calculations
 */
export function getDynamicColumnWidthRatios(columnModels: OwaDate[]): ColumnWidthRatio[] {
    let numDays = columnModels.length; // nD in design logic
    let todayIndex = -1;
    columnModels.forEach((model: OwaDate, index: number) => {
        if (isToday(model)) {
            todayIndex = index;
        }
    });

    let relativeWidths: number[];
    if (todayIndex < 0) {
        // if today not in the week, just get the default
        relativeWidths = columnModels.map(() => 1 / columnModels.length);
    } else {
        relativeWidths = getColumnWidths(numDays, todayIndex);
    }

    let results = [];
    let edge = 0;
    for (let i = 0; i < numDays; i++) {
        let nextEdge = edge + relativeWidths[i];
        results.push({ left: edge, right: 1 - nextEdge });
        edge = nextEdge;
    }

    return results;
}

function getColumnWidths(totalDays: number, todayIndex: number): number[] {
    const todayWidthMultiplier = 1.5; // multiplier for 'today' relative to if every column was even width
    const tmwWidthMultiplier = 1.3; // multiplier for 'tomorrow' relative to if every column was even width
    const futureWidthRelativeMultiplier = 1.5; // multiplier for future days (past tmw) relative to past days (so only used once space taken for today, tmw)

    let widths = {} as ColumnWidths;
    widths.today = todayWidthMultiplier / totalDays;
    widths.tomorrow = totalDays > todayIndex + 1 ? tmwWidthMultiplier / totalDays : 0;

    const pastDayCount = todayIndex;
    const futureDayCount = Math.max(0, totalDays - todayIndex - 2); // minus 2 for today and tomorrow
    const totalSpaceRemaining = 1.0 - widths.today - widths.tomorrow;

    widths.past =
        totalSpaceRemaining / (futureWidthRelativeMultiplier * futureDayCount + pastDayCount);
    widths.future =
        (totalSpaceRemaining * futureWidthRelativeMultiplier) /
        (futureWidthRelativeMultiplier * futureDayCount + pastDayCount);

    let result = [];

    // build the array to consume
    for (let i = 0; i < totalDays; i++) {
        if (i < todayIndex) {
            result.push(widths.past);
        } else if (i == todayIndex) {
            result.push(widths.today);
        } else if (i == todayIndex + 1) {
            result.push(widths.tomorrow);
        } else {
            result.push(widths.future);
        }
    }

    let totalSize = result.reduce((previousValue, currentValue) => previousValue + currentValue);

    if (totalSize != 1) {
        result = result.map(value => value / totalSize);
    }

    return result;
}

interface ColumnWidths {
    past: number;
    today: number;
    tomorrow: number;
    future: number;
}
